import { DownOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd5';
import { type MenuItemType } from 'antd5/es/menu/interface';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Tooltip } from 'components/ui';
import {
  type InitialStep,
  Steps,
  UploadInspectionDataModal,
  UploadTypes,
} from 'horizon/routes/Inspections/InspectionsUploadManager/UploadInspectionDataModal';
import {
  type AtlasGqlAssetType,
  type AtlasGqlInspectionTask,
  type AtlasGqlInspectionType,
  type AtlasGqlOrganization,
  type AtlasGqlTask,
  AtlasGqlWorkContainerTypeOption,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { getDisplayStatus, isInDeliverableStatus } from 'utils/inspectionStatus';
import { ENHANCED_INSPECTION_STATUSES } from 'utils/release-toggles';

import { DeliverInspections } from './DeliverInspectionsModal';

type InspectionActionsProps = {
  allTasks: AtlasGqlTask[];
  selected: AtlasGqlTask[];
  isUploadVisible?: boolean;
  isDeliverVisible?: boolean;
};

export const InspectionActions: React.FC<InspectionActionsProps> = ({
  allTasks,
  selected,
  isUploadVisible = false,
  isDeliverVisible = false,
}) => {
  const { t } = useTranslation();
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [deliverModalOpen, setDeliverModalOpen] = useState<boolean>(false);
  const { hasReleaseToggle } = useAccountContext();
  const hasEnhancedStatuses = hasReleaseToggle(ENHANCED_INSPECTION_STATUSES);

  // validation logic for Upload Inspections
  const uploadValidation = useMemo(() => {
    // disable button for when there are no inspection tasks
    if (allTasks.every(task => task.type.type !== AtlasGqlWorkContainerTypeOption.InspectionTask)) {
      return {
        visible: true,
        disabled: true,
        tooltip: t(
          'workOrder.noInspectionTasksOnWorkOrder',
          'There are no Inspection Tasks on this Work Order'
        ),
        initialStep: Steps.SummaryUpload,
      };
    }

    // disable button if selected tasks include non-inspection tasks
    if (selected.some(({ type }) => type.type !== AtlasGqlWorkContainerTypeOption.InspectionTask)) {
      return {
        visible: true,
        disabled: true,
        tooltip: t(
          'workOrder.cannotUploadInspectionsNonInspectionTasks',
          'Cannot upload Inspections for Non-Inspection Tasks'
        ),
        initialStep: Steps.SummaryUpload,
      };
    }

    const tasks = selected.length ? selected : allTasks;
    const _inspectionTasks = tasks.filter(
      task => task.type.type === AtlasGqlWorkContainerTypeOption.InspectionTask
    ) as AtlasGqlInspectionTask[];
    const idsOfTasksWithoutInspections = _inspectionTasks
      .filter(({ inspection }) => !inspection)
      .map(({ id }) => id);
    const existingInspectionIds = _inspectionTasks
      .map(({ inspection }) => inspection?.id)
      .filter((inspectionId): inspectionId is string => !!inspectionId);
    const inspectionTasks = _inspectionTasks.map(task => {
      const { id, inspection, asset } = task;
      return {
        taskId: id,
        inspectionId: inspection?.id,
        inspectionType: inspection?.type ?? undefined,
        assetType: asset.assetType ?? undefined,
      };
    });

    // disable button if selected tasks are on different asset types
    const taskAssetTypes = _inspectionTasks
      .map(({ asset }) => asset.assetType)
      .filter((type): type is AtlasGqlAssetType => !!type);
    if (!taskAssetTypes.every(type => taskAssetTypes[0].name === type.name)) {
      return {
        visible: true,
        disabled: true,
        tooltip: t(
          'workOrder.cannotUploadInspectionsDifferentAssetTypes',
          'Cannot upload Inspections of different Asset Types'
        ),
        initialStep: Steps.SummaryUpload,
      };
    }

    // disable button if selected tasks have existing inspections with different inspection types
    const taskInspectionTypes = _inspectionTasks
      .map(({ inspection }) => inspection?.type)
      .filter((type): type is AtlasGqlInspectionType => !!type);
    if (!taskInspectionTypes.every(type => taskInspectionTypes[0].name === type.name)) {
      return {
        visible: true,
        disabled: true,
        tooltip: t(
          'workOrder.cannotUploadInspectionsDifferentInspectionTypes',
          'Cannot upload Inspections of different Inspection Types'
        ),
        initialStep: Steps.SummaryUpload,
      };
    }

    let initialStep = Steps.SummaryUpload;
    if (!idsOfTasksWithoutInspections.length) {
      initialStep = Steps.DetailsUpload;
    }

    return {
      visible: true,
      disabled: !idsOfTasksWithoutInspections.length && !inspectionTasks.length,
      tooltip: '',
      existingInspectionIds,
      inspectionTasks,
      initialStep,
      initialInspectionType: taskInspectionTypes.length ? taskInspectionTypes[0] : undefined,
    };
  }, [allTasks, selected, t]);

  // validation logic for Deliver Inspections
  const deliverValidation = useMemo(() => {
    // disable button for when there are no valid inspection tasks
    const validInspectionTasks = allTasks.filter(_task => {
      const task = _task as AtlasGqlInspectionTask;

      // check for valid task (inspection task with forOrg)
      if (task.type.type !== AtlasGqlWorkContainerTypeOption.InspectionTask || !task.forOrg) {
        return false;
      }

      // check for valid task (released inspection)
      if (
        !task.inspection ||
        !isInDeliverableStatus(getDisplayStatus(task.inspection.status, hasEnhancedStatuses))
      ) {
        return false;
      }

      // valid inspection task
      return true;
    }) as AtlasGqlInspectionTask[];
    if (validInspectionTasks.length === 0) {
      return {
        visible: true,
        disabled: true,
        tooltip: t(
          'workOrder.noValidInspectionsToDeliver',
          'There are no valid Inspections to deliver on this Work Order (Inspection Tasks must have a For Org and an Inspection in a deliverable status)'
        ),
      };
    }

    const selectedTaskIds = selected.map(task => task.id) as string[];
    const selectedTasks = selectedTaskIds.length
      ? allTasks.filter(task => selectedTaskIds.includes(task.id))
      : allTasks;

    // disable button if selected tasks include non-inspection tasks
    const hasInvalidTasks = !selectedTasks.every(task => {
      return task.type.type === AtlasGqlWorkContainerTypeOption.InspectionTask;
    });
    if (hasInvalidTasks) {
      return {
        visible: true,
        disabled: true,
        tooltip: t(
          'workOrder.cannotDeliverInspectionsNonInspectionTasks',
          'Cannot deliver Inspections for Non-Inspection Tasks'
        ),
      };
    }

    // disable button if selected tasks have different for orgs
    const hasInvalidForOrgs = !selectedTasks.every(task => {
      const initialForOrg = (selectedTasks[0] as AtlasGqlInspectionTask).forOrg;
      const forOrg = (task as AtlasGqlInspectionTask).forOrg;
      return !!forOrg?.id && forOrg?.id === initialForOrg?.id;
    });
    if (hasInvalidForOrgs) {
      return {
        visible: true,
        disabled: true,
        tooltip: t(
          'workOrder.cannotDeliverInspectionsInvalidForOrgs',
          'Cannot deliver Inspections with invalid or different For Orgs'
        ),
      };
    }

    // disable button if selected tasks do not all have released inspections
    const hasInvalidInspections = !selectedTasks.every(task => {
      const inspection = (task as AtlasGqlInspectionTask).inspection;
      return (
        !!inspection?.id &&
        isInDeliverableStatus(getDisplayStatus(inspection?.status, hasEnhancedStatuses))
      );
    });
    if (hasInvalidInspections) {
      return {
        visible: true,
        disabled: true,
        tooltip: t(
          'workOrder.cannotDeliverInspectionsNotCreatedOrNotDeliverable',
          'Cannot deliver Inspections that are not yet created or are not in a deliverable status'
        ),
      };
    }

    const inspectionTasks = selectedTasks as AtlasGqlInspectionTask[];

    return {
      visible: true,
      disabled: false,
      tooltip: '',
      inspectionTasks,
      forOrg: inspectionTasks[0]?.forOrg as AtlasGqlOrganization | undefined,
    };
  }, [allTasks, selected, hasEnhancedStatuses, t]);

  // build menu items
  const menuItems: MenuItemType[] = useMemo(() => {
    const items: MenuItemType[] = [];

    if (isUploadVisible) {
      items.push({
        key: 'upload',
        label: uploadValidation.disabled ? (
          <Tooltip title={uploadValidation.tooltip}>
            <span>{t('workOrder.uploadInspections', 'Upload Inspections')}</span>
          </Tooltip>
        ) : (
          t('workOrder.uploadInspections', 'Upload Inspections')
        ),
        disabled: uploadValidation.disabled,
        onClick: () => {
          if (!uploadValidation.disabled) {
            setUploadModalOpen(true);
          }
        },
      });
    }

    if (isDeliverVisible) {
      items.push({
        key: 'deliver',
        label: deliverValidation.disabled ? (
          <Tooltip title={deliverValidation.tooltip}>
            <span>{t('workOrder.deliverInspections', 'Deliver Inspections')}</span>
          </Tooltip>
        ) : (
          t('workOrder.deliverInspections', 'Deliver Inspections')
        ),
        disabled: deliverValidation.disabled,
        onClick: () => {
          if (!deliverValidation.disabled) {
            setDeliverModalOpen(true);
          }
        },
      });
    }

    return items;
  }, [isUploadVisible, isDeliverVisible, uploadValidation, deliverValidation, t]);

  return (
    <>
      <Dropdown menu={{ items: menuItems }} trigger={['click']}>
        <Button data-testid="inspection-actions-dropdown">
          {t('table.inspections', 'Inspections')} <DownOutlined />
        </Button>
      </Dropdown>
      {isUploadVisible && (
        <UploadInspectionDataModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          lockedUploadType={UploadTypes.Multiple}
          initialStep={uploadValidation.initialStep as InitialStep}
          initialInspectionType={uploadValidation.initialInspectionType}
          existingInspectionIds={uploadValidation.existingInspectionIds ?? []}
          inspectionTasks={uploadValidation.inspectionTasks ?? []}
          requireTaskId={true}
        />
      )}
      {isDeliverVisible && (
        <DeliverInspections
          open={deliverModalOpen}
          onClose={() => setDeliverModalOpen(false)}
          inspectionTasks={deliverValidation.inspectionTasks ?? []}
          forOrg={deliverValidation.forOrg}
        />
      )}
    </>
  );
};
